import { allPieceTypes, largePieceTypes, predatorPieceTypes } from "../const";
import { Move } from "../Move";
import {
  IPiece,
  PieceMovement,
  Position,
  Direction,
  IMove,
  MoveType,
  ISquare,
  ISideEffect,
  PieceType,
} from "../types";
import { BaseMovement } from "./BaseMovement";
import { SideEffect } from "../SideEffect";
import { Effect, EffectType } from "../types";

const invertedDirections: [number, number][] = [
  [1, -1], // up-left
  [1, 0], // up
  [1, 1], // up-right
  [0, -1], // left
  [0, 1], // right
  [-1, -1], // down-left
  [-1, 0], // down
  [-1, 1], // down-right
];

export class PenguinMovement extends BaseMovement implements PieceMovement {
  private directionToIndex: Record<Direction, number> = {
    "up-left": 0,
    up: 1,
    "up-right": 2,
    left: 3,
    right: 4,
    "down-left": 5,
    down: 6,
    "down-right": 7,
  };

  getValidMoves(
    piece: IPiece,
    position: Position,
    board: ISquare[][],
    isLightTurn?: boolean,
    gameStateManager?: any
  ): IMove[] {
    const possibleMoves: IMove[] = [];

    // Check if zombified - this reverses the bonus turn precedent
    const isZombified = piece.zombifiedStatus?.isZombified || false;

    // ZOMBIFICATION: Zombified pieces act as the opposite color
    const effectiveIsLight = isZombified
      ? !piece.isLight
      : piece.isLight;

    // Check if this piece has bonus turn active by checking gameState
    const hasBonusTurn = gameStateManager?.getGameState?.()?.activeBonusTurnPieceId === piece.id;

    // ZOMBIFIED PENGUIN REVERSE ORDER:
    // Normal: slide/capture (grants bonus) → rotate (ends turn)
    // Zombified: rotate (grants bonus) → slide/capture (ends turn)

    if (isZombified && !hasBonusTurn) {
      // ZOMBIFIED FIRST SELECTION: Only show rotations (skip slide/capture generation)
      // The rotation will grant bonus turn, then slide/capture will be available
    } else if (!isZombified && hasBonusTurn) {
      // NORMAL PENGUIN BONUS TURN: Only rotations allowed (no slide/capture)
      // After sliding or capturing, the penguin can only rotate or end turn
    } else {
      // NORMAL FIRST SELECTION or ZOMBIFIED BONUS TURN: Generate slide/capture moves
      if (isZombified && hasBonusTurn) {
      }
      // Get the furthest square in the direction the Penguin is facing
      const direction = piece.direction;
      if (direction) {
        this.getValidSlideMove(piece, position, board, possibleMoves, direction, isZombified, effectiveIsLight);
      }

      // Get any pieces within 1 square that the Penguin can capture
      this.getValidCaptureMoves(piece, position, board, possibleMoves, isZombified, effectiveIsLight);
    }

    // ROTATE MOVES - Generate for regular penguins (first selection + bonus turn) and zombified first selection
    // Don't generate rotations if zombified AND in bonus turn (only slide/capture available)
    if (!(isZombified && hasBonusTurn)) {
      let rotateCount = 0;
      const rotateDirections: [number, number, Direction][] = [
        [1, -1, "up-left"],
        [1, 0, "up"],
        [1, 1, "up-right"],
        [0, -1, "left"],
        [0, 1, "right"],
        [-1, -1, "down-left"],
        [-1, 0, "down"],
        [-1, 1, "down-right"],
      ];

      rotateDirections.forEach(([dRow, dCol, dir]) => {
        // Skip the direction the penguin is already facing — rotating to the
        // current facing is a no-op and the highlight stacks on top of the
        // forward slide-move highlight, hiding the slide click target.
        if (dir === piece.direction) return;

        const rotateTargetPos = {
          row: position.row + dRow,
          col: position.col + dCol,
        };

        // Create rotation if target position is on board - rotation is just a facing change, not movement,
        // so occupied squares don't block it. Zombified penguins can rotate to ALL 8 directions.
        if (isZombified || this.onBoard(board, rotateTargetPos)) {
          // Create rotation move
          // Position on target square for UI indicators to render AROUND the penguin
          const move: IMove = {
            id: `${piece.id}-rotate-${dir}` as IMove["id"],
            pieceId: piece.id,
            position: rotateTargetPos, // Always on target square (around penguin)
            toPiece: null,
            moveType: MoveType.rotate,
            sideEffects: [],
          };

          // Add rotation side effect on target position (for UI rendering)
          const sideEffect = new SideEffect(
            move.id,
            rotateTargetPos,
            [],
            { type: EffectType.rotate }
          ) as ISideEffect & { rotateDirection?: Direction };
          sideEffect.rotateDirection = dir;
          move.sideEffects.push(sideEffect);

          // CRITICAL: Only zombified penguins get bonus turn from rotation
          if (isZombified) {
            move.sideEffects.push(
              new SideEffect(
                move.id,
                position,
                [piece],
                Effect.BonusTurn()
              ) as ISideEffect
            );
          }

          possibleMoves.push(move);
          rotateCount++;
        }
      });

      if (isZombified) {
      }
    }

    // Add endTurn move option (for bonus turns when penguin chooses not to rotate)
    const endTurnMove: IMove = {
      id: `${piece.id}-end-turn` as IMove["id"],
      pieceId: piece.id,
      position: { ...position }, // Stay in same position
      toPiece: null,
      moveType: MoveType.endTurn,
      sideEffects: [],
    };
    possibleMoves.push(endTurnMove);

    return possibleMoves;
  }

  private getValidSlideMove(
    piece: IPiece,
    position: Position,
    board: ISquare[][],
    possibleMoves: IMove[],
    direction: Direction,
    isZombified: boolean,
    effectiveIsLight: boolean
  ) {
    const directionIndex = this.directionToIndex[direction];
    const [rowDiff, colDiff] = invertedDirections[directionIndex];
    let newPosition = { ...position };


    while (true) {
      const nextPosition = {
        col: newPosition.col + colDiff,
        row: newPosition.row + rowDiff,
      };


      if (!this.onBoard(board, nextPosition)) {
        break;
      }

      const nextSquare = board[nextPosition.row][nextPosition.col];
      const squareInfo = this.getSquareInfo(nextSquare, effectiveIsLight);

      if (squareInfo.basicSquareInfo?.piece) {
        break;
      }

      newPosition = nextPosition;
    }


    // Add the slide move to the possible moves array
    const toSquare = board[newPosition.row][newPosition.col];
    const toSquareInfo = this.getSquareInfo(toSquare, effectiveIsLight);

    if (toSquareInfo.basicSquareInfo) {
      const move = this.getMove(
        position,
        newPosition,
        toSquareInfo.basicSquareInfo,
        piece,
        [] // Penguin can't capture during slide move
      );


      if (move) {
        // Add bonus turn for slide move (but NOT if zombified - reverse precedent)
        if (!isZombified) {
          move.addSideEffect(newPosition, [piece], Effect.BonusTurn());
        }
        possibleMoves.push(move);
      }
    } else {
    }
  }

  private getValidCaptureMoves(
    piece: IPiece,
    position: Position,
    board: ISquare[][],
    possibleMoves: IMove[],
    isZombified: boolean,
    effectiveIsLight: boolean
  ) {
    const capturablePieceTypes = allPieceTypes.filter(
      (pieceType) =>
        !largePieceTypes.includes(pieceType) &&
        !predatorPieceTypes.includes(pieceType)
    );

    for (const [rowDiff, colDiff] of invertedDirections) {
      const newPosition = {
        col: position.col + colDiff,
        row: position.row + rowDiff,
      };
      if (!this.onBoard(board, newPosition)) continue;

      const newSquare = board[newPosition.row][newPosition.col];
      const squareInfo = this.getSquareInfo(newSquare, effectiveIsLight);

      if (squareInfo.basicSquareInfo) {
        const move = this.getMove(
          position,
          newPosition,
          squareInfo.basicSquareInfo,
          piece,
          capturablePieceTypes
        );

        if (move && move.moveType === MoveType.capture) {
          const capturedPiece = squareInfo.basicSquareInfo.piece;

          // CRITICAL: Don't add bonus turn if:
          // 1. Zombified (reverse precedent - rotation grants bonus instead)
          // 2. Capturing Fungi (Fungi steals the rotate ability)
          if (!isZombified && capturedPiece?.type !== PieceType.Fungi) {
            move.addSideEffect(newPosition, [piece], Effect.BonusTurn());
          }

          possibleMoves.push(move);
        }
      }
    }
  }
}
